/**
 * Labstep
 *
 * @module components/FilterPost
 * @desc Main Filter Post component
 */

import classNames from 'classnames';
import EntityViewActionSave from 'labstep-web/components/EntityView/Action/Save';
import ExperimentWorkflowViewManager from 'labstep-web/components/ExperimentWorkflow/ViewManager';
import { FilterMenu } from 'labstep-web/components/Filter/Menu';
import { FilterSearch as SearchBarFilter } from 'labstep-web/components/Filter/Search';
import PostFilterActive from 'labstep-web/components/FilterPost/Active';
import { ICONS } from 'labstep-web/constants/icons';
import { useGridServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { Button } from 'labstep-web/core/Button';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { DataGridExportAsCSV } from 'labstep-web/grid/Index/components/DataGridExportAsCSV';
import { useExportProgressContext } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridIndexActionShowSideBar as EntityDataGridActionShowSideBar } from 'labstep-web/grid/Index/components/ShowSideBar';
import { getHumanReadableEntityName } from 'labstep-web/services/i18n.service';
import { objectOrFunction } from 'labstep-web/services/react.service';
import type React from 'react';
import { useEffect } from 'react';
import type { SemanticICONS } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type { FilterPostProps } from './types';

export const SearchBarAction: React.FC<{
  text: string;
  icon: SemanticICONS;
  onClick?: VoidFunction;
  disabled?: boolean;
  warning?: boolean;
  className?: string;
}> = ({ text, icon, onClick, disabled, warning, className }) => (
  <Button
    className={
      className
        ? className
        : classNames(styles.searchBarAction, {
            [styles.warning]: warning,
          })
    }
    onClick={onClick}
    disabled={disabled}
  >
    <Icon name={icon} />
    {text}
  </Button>
);

export const FilterPost: React.FC<FilterPostProps> = ({
  action,
  entityName,
  searchBarProps,
  isTemplate,
  searchParams,
  filters,
  hideSearch = false,
  searchKey = 'search_query',
  readNextPage,
  showExperimentWorkflowViewManager,
}) => {
  const filterButton = filters && filters.length > 0 && (
    <FilterMenu
      trigger={
        <SearchBarAction text="Filter" icon={ICONS.filter.primary} />
      }
      entityName={entityName}
      filters={filters}
      clearAll={() => {}}
    />
  );

  const gridService = useGridServiceFromContext();
  const exportProgressContext = useExportProgressContext();

  useEffect(() => {
    if (exportProgressContext) {
      exportProgressContext.setExportProgress((prev) => ({
        ...prev,
        loadMore: readNextPage,
        gridService,
      }));
    }
  }, [gridService, readNextPage]);

  return (
    <div
      data-testid="filter"
      className={hideSearch ? styles.hideSearch : ''}
    >
      <div className={styles.searchBarContainer}>
        <div
          data-testid="search-bar"
          className={styles.searchBar}
          data-cy="components/Filter:searchBar"
        >
          {showExperimentWorkflowViewManager && (
            <ExperimentWorkflowViewManager />
          )}
          <SearchBarFilter
            entityName={entityName}
            hideSearch={hideSearch}
            searchBarProps={{
              label: (
                <div className={styles.searchBarActionContainer}>
                  {filterButton}
                  <EntityDataGridActionShowSideBar
                    className={styles.searchBarAction}
                  />
                  {exportProgressContext && <DataGridExportAsCSV />}
                  <EntityViewActionSave />
                </div>
              ),
              placeholder: `Search for ${getHumanReadableEntityName(
                entityName,
                true,
                false,
                isTemplate,
              )}`,
              ...searchBarProps,
            }}
            searchKey={searchKey}
          />

          <Flex spacing="gap">
            {objectOrFunction(action, {
              searchParams,
            })}
          </Flex>
        </div>
      </div>
      <Flex grow>
        <PostFilterActive searchParams={searchParams} />
      </Flex>
    </div>
  );
};
